import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { CheckCircle2, CircleSlash, Pencil } from "lucide-react";
import { toast } from "sonner";
import type { OrderExpeditionListItem } from "@/lib/types";

export type ExpeditionMode = "CARREGADO" | "EM_FALTA" | "EDIT";

function fmtQty(n: number): string {
  return n.toFixed(3).replace(/\.?0+$/, "");
}

interface Props {
  item: OrderExpeditionListItem;
  mode: ExpeditionMode;
  onCancel: () => void;
  onSubmit: (input: { loadedQuantity: number; reason?: string }) => Promise<void>;
}

export function ExpeditionDecisionModal({ item, mode, onCancel, onSubmit }: Props) {
  const requested = Number(item.requestedQuantity);
  // Teto de carga = o que a produção entregou (quando há decisão). Sem decisão, usa o pedido.
  const produced = item.producedQuantity == null ? null : Number(item.producedQuantity);
  const maxLoad = produced ?? requested;
  const defaultQty =
    mode === "EM_FALTA"
      ? String(item.loadedQuantity ?? 0)
      : String(item.loadedQuantity ?? maxLoad);
  const [quantity, setQuantity] = useState(defaultQty);
  const [reason, setReason] = useState(item.reason ?? "");
  const [submitting, setSubmitting] = useState(false);

  const reasonRequired = mode === "EM_FALTA";

  const submit = async () => {
    const q = Number(String(quantity).replace(",", "."));
    if (!Number.isFinite(q) || q < 0) {
      toast.error("Quantidade inválida");
      return;
    }
    // A quantidade conferida pode divergir do produzido (para mais ou para menos):
    // registra-se o que de fato foi expedido. O produzido/pedido fica só como referência.
    const trimmedReason = reason.trim();
    if (reasonRequired && !trimmedReason) {
      toast.error("Informe o motivo da falta");
      return;
    }
    setSubmitting(true);
    try {
      await onSubmit({ loadedQuantity: q, reason: trimmedReason || undefined });
    } finally {
      setSubmitting(false);
    }
  };

  const config = {
    CARREGADO: { title: "Carregar item", icon: CheckCircle2, label: `Quantidade carregada (${item.unit})`, cta: "Confirmar carga" },
    EM_FALTA: { title: "Item em falta", icon: CircleSlash, label: `Quanto foi carregado? (0 se nada) (${item.unit})`, cta: "Registrar falta" },
    EDIT: { title: "Editar quantidade", icon: Pencil, label: `Quantidade carregada (${item.unit})`, cta: "Salvar" },
  }[mode];
  const Icon = config.icon;

  return (
    <Dialog open onOpenChange={(open) => !open && onCancel()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Icon className="h-4 w-4" /> {config.title}
          </DialogTitle>
          <DialogDescription>
            {item.productName} · pedido <strong>{item.requestedQuantity}</strong> {item.unit}
            {produced != null && (
              <> · produzido <strong>{fmtQty(produced)}</strong> {item.unit}</>
            )}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <label className="mb-1 block text-xs font-medium text-muted-foreground">{config.label}</label>
            <Input
              type="number"
              min="0"
              step="0.001"
              value={quantity}
              onChange={(event) => setQuantity(event.target.value)}
              autoFocus
            />
            {mode !== "EM_FALTA" && (
              <p className="mt-1 text-[11px] text-muted-foreground">
                Referência: {fmtQty(maxLoad)} {item.unit} {produced != null ? "(produzido)" : "(pedido)"} — informe o
                que foi realmente expedido
              </p>
            )}
          </div>
          {reasonRequired && (
            <div>
              <label className="mb-1 block text-xs font-medium text-muted-foreground">
                Motivo <span className="text-destructive">*</span>
              </label>
              <Textarea
                value={reason}
                onChange={(event) => setReason(event.target.value)}
                placeholder="Por que está em falta?"
                className="min-h-[72px]"
              />
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" size="sm" onClick={onCancel} disabled={submitting}>
            Cancelar
          </Button>
          <Button variant="hero" size="sm" onClick={() => void submit()} disabled={submitting}>
            {config.cta}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
